import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { showNotification } from '../../LanghoutBetonPortal';

import { repeat } from 'lit/directives/repeat.js';
import { ProductStockView } from '../../types/types';
import '../../elements/list-nagivation/list-navigation';
import {
  arraysEqual,
  converStockStatusToInt,
  converStockStatusToReadable,
  ProductStockTypeOnRequest,
  roundUnitNumbers,
  StockStatusInStock,
  StockStatusLimited,
} from '../../util/util';
import {
  getSearchParam,
  getQueryParamURL,
  addHistory,
} from '../../util/list_util';
import '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';
import { DropdownMultiselectElement } from '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';
import { refreshIcon, sortIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-list-stock')
export class LanghoutBetonListStock extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .row {
        display: flex;
        flex-direction: row;
      }

      .row-flex {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .filter-row {
        min-height: 130px;
      }

      .filter-column {
        margin-bottom: 20px;
      }

      .fat {
        font-weight: 900;
        color: var(--menu-color);
      }

      .label {
        font-weight: bold;
      }

      @media (max-width: 701px) {
        .label {
          text-align: left;
        }
      }

      .product-column {
        text-align: left;
        width: 100%;
        min-width: 180px;
        min-height: calc(54px - 24px);
      }

      .account-id-click {
        font-weight: bold;
        text-decoration: underline;
        text-decoration-color: var(--primary-color);
      }

      .account-id-click:hover {
        cursor: pointer;
      }

      .border-bot {
        border-bottom: 2px solid var(--menu-color);
      }

      .column-large {
        text-align: left;
        min-width: calc(350px - 20px);
        max-width: calc(350px - 20px);
      }

      .table-wrapper {
        overflow-x: auto;
      }

      .table-header {
        color: var(--text-color-light);
      }

      .header-column {
        background-color: var(--menu-color);
        padding-left: 12px;
      }

      .column-border-right {
        border-right: 2px solid var(--menu-color);
      }

      .navigation-wrapper {
        margin-top: 20px;
      }

      langhout-beton-view-account {
        position: absolute;
        top: 84px;
        right: -200vw;
      }

      .sort-icon-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .sort-icon {
        transform: rotate(90deg);
      }

      .sort-icon:hover {
        cursor: pointer;
      }

      .sort-active {
        color: var(--text-color-main);
      }

      .account-id-column {
        padding-left: 10px;
        padding-right: 10px;
      }

      .dot {
        height: 25px;
        width: 25px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 10px;
      }

      .dot-red {
        background-color: red;
      }

      .dot-orange {
        background-color: orange;
      }

      .dot-green {
        background-color: var(--primary-color);
      }

      .button-margin {
        margin-top: 20px;
        margin-left: 25px;
      }
    `,
  ];

  private requestCounter = 0;

  private listTimer = 0;

  @property({ type: Array })
  private products: ProductStockView[] = [];

  @property({ type: Number })
  private productCount = 0;

  @query('#code')
  public code?: HTMLInputElement;

  @query('#codeSync')
  public codeSync?: HTMLInputElement;

  @query('#description')
  public description?: HTMLInputElement;

  @query('#dropdownElement')
  public dropdownElement?: DropdownMultiselectElement;

  @property({ type: Array, attribute: true })
  public selected: Array<string> = [];

  private offset = 0;

  private limit = 50;

  private orderByColumn = '';

  private orderByDirection = '';

  constructor() {
    super();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
  }

  protected routerLocationChanged = (): void => {
    this.setPropertiesFromURL();
    this.listWithBackoff(false);
  };

  protected firstUpdated = (): void => {
    if (this.dropdownElement) {
      this.dropdownElement.selectable = [
        'Op voorraad',
        'Voorraad beperkt',
        'Uitverkocht',
      ];
    }
    window.addEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    this.setPropertiesFromURL();
    this.listWithBackoff(false);
  };

  protected setPropertiesFromURL = (): void => {
    const url = getQueryParamURL();
    if (this.code) {
      this.code.value = getSearchParam(url, 'code');
    }
    if (this.description) {
      this.description.value = getSearchParam(url, 'description');
    }
    if (this.dropdownElement) {
      const stockStatuses = getSearchParam(url, 'stock_statuses');
      if (stockStatuses !== '') {
        const res: string[] = [];
        const statuses = stockStatuses.split(',');
        if (statuses.length > 0) {
          statuses.forEach(e => {
            res.push(converStockStatusToReadable(parseInt(e)));
          });
          this.selected = res;
          this.dropdownElement.selected = res;
        }
      } else {
        this.selected = [];
        this.dropdownElement.selected = [];
      }
    }
    this.orderByColumn = getSearchParam(url, 'order_by');
    this.orderByDirection = getSearchParam(url, 'order_direction');
  };

  render() {
    return html`
      ${this.renderFilterRow()}
      <div class="table-wrapper">
        <div class="row table-header">
          <div class="column header-column product-column">
            <div class="row">
              <h3>Artikel</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('code')}"
                @click="${() => {
                  this.orderColumns('code');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Artikelomschrijving</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('description')}"
                @click="${() => {
                  this.orderColumns('description');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Status</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('stock_status')}"
                @click="${() => {
                  this.orderColumns('stock_status');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Voorraad</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('stock_amount')}"
                @click="${() => {
                  this.orderColumns('stock_amount');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>In pickorder</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive(
                  'in_order_amount',
                )}"
                @click="${() => {
                  this.orderColumns('in_order_amount');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>In bestelling</h3>
              <div class="fill"></div>
            </div>
          </div>
        </div>
        ${this.renderRows()}
      </div>
      <div class="navigation-wrapper">
        <list-navigation
          index="${this.offset}"
          limit="${this.limit}"
          total="${this.productCount}"
          @change="${(e: CustomEvent) => {
            if (this.offset != e.detail.value) {
              this.offset = parseInt(e.detail.value);
              this.listWithBackoffWithoutReset();
            }
          }}"
        ></list-navigation>
      </div>
    `;
  }

  renderFilterRow = () => {
    return html`
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Artikelnummer</div>
          <div class="input-field">
            <input
              type="text"
              id="code"
              name="code"
              @input="${() => {
                this.listWithBackoff();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Artikelomschrijving</div>
          <div class="input-field">
            <input
              type="text"
              id="description"
              name="description"
              @input="${() => {
                this.listWithBackoff();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Status</div>
          <div class="input-field">
            <dropdown-multiselect-element
              id="dropdownElement"
              @change="${(e: CustomEvent) => {
                const newSelected = <Array<string>>JSON.parse(e.detail.value);
                if (!arraysEqual(newSelected, this.selected)) {
                  this.selected = newSelected;
                  this.listWithBackoff();
                }
              }}"
            ></dropdown-multiselect-element>
          </div>
        </div>

        <langhout-button
          text="Reset filters"
          class="button-margin"
          @click="${(): void => {
            window.location.href = '/stock';
          }}"
        ></langhout-button>

        <div class="fill"></div>
        <div class="row filter-column"></div>
      </div>

      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Artikelnummer</div>
          <div class="input-field">
            <input type="text" id="codeSync" name="codeSync" />
          </div>
        </div>
        <langhout-button
          text="Product ophalen Exact"
          class="button-margin"
          @click="${(): void => {
            this.syncProductExact();
          }}"
        ></langhout-button>

        <div class="fill"></div>
        <div
          @click="${() => {
            showNotification('Refreshing products');
            this.listWithBackoff();
          }}"
        >
          ${refreshIcon()}
        </div>
      </div>
    `;
  };

  renderRows = () => {
    return html` ${repeat(
      this.products,
      product => product.id,
      (product, index) => {
        let tableColumnClass = 'border-bot';
        if (index == this.products.length - 1) {
          tableColumnClass = '';
        }
        return html`
          <div
            class="row table-row"
            @click="${() => {
              window.location.href = `/view-stock?id=${product.id}`;
            }}"
          >
            <div
              class="row account-id-column table-column account-id-click column-border-right product-column ${tableColumnClass}"
            >
              ${product.code}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${product.description}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${this.renderStatus(product)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${roundUnitNumbers(product.stock_amount).toLocaleString('nl-NL')}
              ${product.unit}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${roundUnitNumbers(
                product.in_order_amount_in_units,
              ).toLocaleString('nl-NL')}
              ${product.unit}
            </div>
            <div class="column table-column product-column ${tableColumnClass}">
              ${roundUnitNumbers(product.to_be_delivered).toLocaleString(
                'nl-NL',
              )}
              ${product.unit}
            </div>
          </div>
        `;
      },
    )}`;
  };

  renderStatus = (product: ProductStockView) => {
    if (product.stock_type === ProductStockTypeOnRequest) {
      return html`Op Aanvraag`;
    }
    if (product.stock_status === StockStatusInStock) {
      return html`
        <div class="row">
          <span class="dot dot-green"></span>
          Op voorraad
        </div>
      `;
    } else if (product.stock_status === StockStatusLimited) {
      return html`
        <div class="row">
          <span class="dot dot-orange"></span>
          Voorraad beperkt
        </div>
      `;
    } else {
      return html`
        <div class="row">
          <span class="dot dot-red"></span>
          Uitverkocht
        </div>
      `;
    }
  };

  isSortActive = (column: string): string => {
    if (this.orderByColumn === column) {
      return 'sort-active';
    }
    return '';
  };

  orderColumns = (column: string) => {
    if (this.orderByColumn === column && this.orderByDirection === 'desc') {
      this.orderByDirection = 'asc';
    } else if (
      this.orderByColumn === column &&
      this.orderByDirection === 'asc'
    ) {
      this.orderByColumn = '';
      this.orderByDirection = '';
    } else {
      this.orderByColumn = column;
      this.orderByDirection = 'desc';
    }
    this.listWithBackoff();
  };

  listWithBackoffWithoutReset = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.listProductsWithStock();
    }, 50);
  };

  listWithBackoff = (recordHistory: boolean = true) => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.offset = 0;
      this.listProductsWithStock(recordHistory);
    }, 50);
  };

  createFilterArray = (): string[] => {
    const filterArray = [];
    const descriptionFilter = this.description ? this.description.value : '';
    if (descriptionFilter != '') {
      filterArray.push(`description=${descriptionFilter}`);
    }
    const statusFilterIntegerArray: Array<number> = [];
    this.selected.forEach(element => {
      statusFilterIntegerArray.push(converStockStatusToInt(element));
    });
    if (statusFilterIntegerArray.length != 0) {
      filterArray.push(`stock_statuses=${statusFilterIntegerArray.join(',')}`);
    }

    const codeFilter = this.code ? this.code.value : '';
    if (codeFilter != '') {
      filterArray.push(`code=${codeFilter}`);
    }
    filterArray.push(`order_by=${this.orderByColumn}`);
    filterArray.push(`order_direction=${this.orderByDirection}`);
    return filterArray;
  };

  listProductsWithStock = async (recordHistory: boolean = true) => {
    this.requestCounter++;
    const requestCounter = this.requestCounter;

    const filterArray = this.createFilterArray();

    let path = `/stock/products?limit=${this.limit}&offset=${this.offset}`;
    const filter = filterArray.join('&');
    if (filter !== '') {
      if (recordHistory) {
        addHistory(filter);
      }
      path += '&' + filter;
    }

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (!isSuccessfulAPIResponse(result)) {
      return;
    }
    if (requestCounter != this.requestCounter) {
      return;
    }
    const xCount = result.headers.get('x-count');
    if (xCount) {
      this.productCount = parseInt(xCount);
    }

    this.products = result.body;
  };

  syncProductExact = async () => {
    if (!this.codeSync) {
      return;
    }
    const itemCode = this.codeSync.value;

    const result = await authenticatedAPICall(
      `/products/${itemCode}`,
      'POST',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      showNotification(
        `Product met code ${itemCode} succesvol gesynchroniseerd`,
      );
      this.codeSync.value = '';
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-list-stock': LanghoutBetonListStock;
  }
}
